/**
 * §7.3 Per-region drilldown. This is where the founder's
 * "culture/customs/climate/transport" catalog fields surface.
 * Pulls from @biomax/plugin-region-database.
 */

import { useMemo, useState } from 'react';
import { findRegion, listRegions, queryCulturalRiskFactors } from '@biomax/plugin-region-database';
import { CulturalRiskSidebar } from '../cultural-risk/primitives';

export function PerRegionDrilldown() {
  const all = listRegions();
  const [id, setId] = useState<string>('usa');
  const region = useMemo(() => findRegion(id) ?? all[0], [id, all]);
  // Phase 9.3 — cultural-risk factors for this jurisdiction (iso3-scoped).
  const culturalFactors = useMemo(
    () => region ? queryCulturalRiskFactors({ iso3: region.names.iso3 }) : [],
    [region],
  );

  if (!region) return <div>Loading regions…</div>;

  const totalUnder18 = region.population.ageStructure
    .filter(b => b.ageBandHigh <= 17)
    .reduce((a, b) => a + b.count, 0);
  const total65Plus = region.population.ageStructure
    .filter(b => b.ageBandLow >= 65)
    .reduce((a, b) => a + b.count, 0);

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.3</span>
        <h1 className="surface__title">{region.names.english}</h1>
      </div>
      <div className="surface__sub">
        {region.names.iso3} · population {(region.population.total / 1e6).toFixed(1)}M ·
        {' '}density {region.population.densityPerKm2?.toFixed(0) ?? '—'}/km² ·
        {' '}urban {((region.population.urbanFraction ?? 0) * 100).toFixed(0)}%
      </div>

      <div style={{ display: 'flex', gap: 'var(--bm-space-3)' }}>
        <label className="simctl__field" style={{ flex: 1 }}>
          <span className="simctl__label">Region</span>
          <select value={id} onChange={e => setId(e.target.value)}>
            {all
              .slice()
              .sort((a, b) => a.names.english.localeCompare(b.names.english))
              .map(r => (
                <option key={r.id} value={r.id}>{r.names.english} ({r.names.iso3})</option>
              ))}
          </select>
        </label>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="kpi__label">Population</div>
          <div className="kpi__value">{(region.population.total / 1e6).toFixed(0)}M</div>
          <div className="kpi__sub">{region.population.year} estimate</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Under 18</div>
          <div className="kpi__value">{(totalUnder18 / 1e6).toFixed(0)}M</div>
          <div className="kpi__sub">{((totalUnder18 / region.population.total) * 100).toFixed(0)}%</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">65+</div>
          <div className="kpi__value">{(total65Plus / 1e6).toFixed(0)}M</div>
          <div className="kpi__sub">{((total65Plus / region.population.total) * 100).toFixed(0)}%</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">ICU beds / 100k</div>
          <div className={`kpi__value ${
            (region.healthcareCapacity?.icuBedsPer100k ?? 0) < 3 ? 'kpi__value--danger'
            : (region.healthcareCapacity?.icuBedsPer100k ?? 0) < 8 ? 'kpi__value--signal'
            : ''}`}>
            {region.healthcareCapacity?.icuBedsPer100k?.toFixed(1) ?? '—'}
          </div>
          <div className="kpi__sub">OECD median ~10</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Hospital beds / 100k</div>
          <div className="kpi__value">{region.healthcareCapacity?.hospitalBedsPer100k?.toFixed(0) ?? '—'}</div>
          <div className="kpi__sub">general + acute</div>
        </div>
      </div>

      <div className="surface__grid">
        <div className="panel">
          <div className="panel__head">
            <span className="panel__title">Age structure</span>
            <span className="panel__meta">{region.population.ageStructure.length} bands</span>
          </div>
          <table className="dtable">
            <thead>
              <tr><th>Band</th><th className="num">Count</th><th className="num">%</th></tr>
            </thead>
            <tbody>
              {region.population.ageStructure.map((b, i) => (
                <tr key={i}>
                  <td>{b.ageBandLow}–{b.ageBandHigh === 200 ? '∞' : b.ageBandHigh}</td>
                  <td className="num">{(b.count / 1e6).toFixed(1)}M</td>
                  <td className="num">{(b.fraction * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {region.cultural && (
          <div className="panel">
            <div className="panel__head">
              <span className="panel__title">Cultural · behavioural</span>
              <span className="panel__meta">§4.2 catalog fields</span>
            </div>
            <table className="dtable">
              <tbody>
                <tr><td>Primary languages</td><td>{region.cultural.primaryLanguages.join(', ')}</td></tr>
                <tr><td>Median household size</td><td className="num">{region.cultural.householdSizeMedian?.toFixed(1) ?? '—'}</td></tr>
                <tr><td>Multigenerational</td><td className="num">{((region.cultural.multigenerationalFraction ?? 0) * 100).toFixed(0)}%</td></tr>
                <tr><td>Greeting norm</td><td>{region.cultural.greetingNorm ?? '—'}</td></tr>
                <tr><td>Mask acceptance</td><td>{region.cultural.maskAcceptance ?? '—'}</td></tr>
                <tr><td>Vaccine hesitancy</td><td className="num">{((region.cultural.vaccineHesitancyIndex ?? 0) * 100).toFixed(0)}%</td></tr>
              </tbody>
            </table>
            {region.cultural.majorMassGatherings && region.cultural.majorMassGatherings.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div className="kpi__label">Major mass gatherings</div>
                {region.cultural.majorMassGatherings.map((g, i) => (
                  <div key={i} style={{ fontSize: 12, padding: '4px 0' }}>
                    <strong>{g.name}</strong>
                    {g.annual ? ` · ${g.annual}/yr` : ''}
                    {g.monthly ? ` · ${g.monthly}/mo` : ''}
                    {g.size ? ` · ${(g.size).toLocaleString()} attendees` : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {region.healthcareCapacity && (
          <div className="panel">
            <div className="panel__head">
              <span className="panel__title">Healthcare capacity</span>
              <span className="panel__meta">per 100k / per 1000</span>
            </div>
            <table className="dtable">
              <tbody>
                <tr><td>ICU beds / 100k</td><td className="num">{region.healthcareCapacity.icuBedsPer100k?.toFixed(1) ?? '—'}</td></tr>
                <tr><td>Ventilators / 100k</td><td className="num">{region.healthcareCapacity.ventilatorsPer100k?.toFixed(1) ?? '—'}</td></tr>
                <tr><td>Hospital beds / 100k</td><td className="num">{region.healthcareCapacity.hospitalBedsPer100k?.toFixed(0) ?? '—'}</td></tr>
                <tr><td>HCW / 1000</td><td className="num">{region.healthcareCapacity.hcwsPer1000?.toFixed(1) ?? '—'}</td></tr>
              </tbody>
            </table>
          </div>
        )}

        {region.climateZone && (
          <div className="panel">
            <div className="panel__head">
              <span className="panel__title">Climate</span>
              <span className="panel__meta">Köppen</span>
            </div>
            <div style={{ fontSize: 14, fontFamily: 'var(--bm-font-mono)' }}>
              {region.climateZone.koppen ?? '—'}
            </div>
          </div>
        )}

        {region.mobilityNodes?.majorAirports && region.mobilityNodes.majorAirports.length > 0 && (
          <div className="panel">
            <div className="panel__head">
              <span className="panel__title">Major airports</span>
              <span className="panel__meta">annual pax</span>
            </div>
            <table className="dtable">
              <thead><tr><th>IATA</th><th className="num">Annual pax (M)</th></tr></thead>
              <tbody>
                {region.mobilityNodes.majorAirports.map((a, i) => (
                  <tr key={i}>
                    <td><span style={{ fontFamily: 'var(--bm-font-mono)' }}>{a.iata}</span></td>
                    <td className="num">{a.annualPaxMillions?.toFixed(1) ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {region.legalAuthority && (
          <div className="panel">
            <div className="panel__head">
              <span className="panel__title">Legal authority</span>
              <span className="panel__meta">mandate scope</span>
            </div>
            <table className="dtable">
              <tbody>
                <tr><td>Masking</td><td>{region.legalAuthority.canMandateMasking ?? '—'}</td></tr>
                <tr><td>Vaccination</td><td>{region.legalAuthority.canMandateVaccination ?? '—'}</td></tr>
                <tr><td>Quarantine</td><td>{region.legalAuthority.canMandateQuarantine ?? '—'}</td></tr>
                <tr><td>Borders</td><td>{region.legalAuthority.canCloseBorders ?? '—'}</td></tr>
              </tbody>
            </table>
            {region.ihrFocalPoint && (
              <div style={{ marginTop: 12, fontSize: 12 }}>
                <div className="kpi__label">IHR National Focal Point</div>
                <div style={{ marginTop: 4 }}><strong>{region.ihrFocalPoint.agencyName}</strong></div>
              </div>
            )}
          </div>
        )}

        {region.vulnerableSubpopulations && region.vulnerableSubpopulations.length > 0 && (
          <div className="panel">
            <div className="panel__head">
              <span className="panel__title">Vulnerable subpopulations</span>
              <span className="panel__meta">equity lens</span>
            </div>
            {region.vulnerableSubpopulations.map((v, i) => (
              <div key={i} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{v.name}</div>
                <div className="kpi__sub">{v.size.toLocaleString()} · {v.reason}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Phase 9.3 — Cultural / political context sidebar (CD R10).
          Region-scoped: factors recorded for this jurisdiction's iso3. */}
      <div style={{ marginTop: 'var(--bm-space-4)' }}>
        <CulturalRiskSidebar
          factors={culturalFactors}
          iso3={region.names.iso3}
          title={`Cultural / political context · ${region.names.english}`}
        />
      </div>
    </div>
  );
}
